import {
    OcrBatchFileResponse,
    OcrBatchIdResponse,
    OcrBatchResponse,
    OcrFileExportFormat,
    OcrOptions,
    OcrProcessedFile
} from '../models/ocr.models';
import {Observable} from "rxjs";
import authorizedFetch from "../core/authorizedFetch";
import axios from "axios";
import i18n from "../i18n";
import {env} from "../config/environment";

const BASE_URL = env.backendUrl;

function buildFormData(files: File[], options: OcrOptions): FormData {
    const formData = new FormData();
    files.forEach(file => formData.append("images", file));

    const data = JSON.stringify({
        'mode': String(options.mode),
        'grounding': Boolean(options.grounding),
        'includeCaption': Boolean(options.include_caption),
        'baseSize': Number(options.base_size),
        'imageSize': Number(options.image_size),
        'cropMode': Boolean(options.crop_mode),
        'testCompress': Boolean(options.test_compress)
    });

    const blob = new Blob([data], { type: 'application/json' });

    formData.append('request', blob);

    return formData;
}

export const ocrService = {
    createOcrBatch: async (): Promise<OcrBatchIdResponse> => {
        const response = await axios.post<OcrBatchIdResponse>('/ocr/conversation');
        return response.data;
    },
    getOcrBatch: async (id: number): Promise<OcrBatchResponse> => {
        const response = await axios.get<OcrBatchResponse>(`/ocr/conversation/${id}`);
        return response.data;
    },
    getOcrBatchFile: async (batchId: number, fileId: number): Promise<OcrBatchFileResponse> => {
        const response = await axios.get<OcrBatchFileResponse>(`/ocr/conversation/${batchId}/file/${fileId}`);
        return response.data;
    },

    processOcrBatch: (id: number, files: File[], options: OcrOptions): Observable<OcrProcessedFile> => {
        return new Observable(subscriber => {
            const lang = i18n.language || 'pl';
            const controller = new AbortController();
            const params = new URLSearchParams({
                conversationId: String(id),
                lang,
            });
            const url = `${BASE_URL}/ocr/process?${params.toString()}`;

            authorizedFetch(url, {
                method: 'POST',
                body: buildFormData(files, options),
                headers: { Accept: "text/event-stream" },
                signal: controller.signal,
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }
                    if (!response.body) {
                        throw new Error("No response body");
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = "";

                    const pump = (): Promise<void> =>
                        reader.read().then(({ done, value }) => {
                            if (done) {
                                subscriber.complete();
                                return;
                            }

                            buffer += decoder.decode(value, { stream: true });

                            const lines = buffer.split("\n");
                            buffer = lines.pop() ?? "";

                            for (const line of lines) {
                                if (!line.startsWith("data:")) continue;

                                const raw = line.slice("data:".length).trim();
                                if (!raw) continue;

                                try {
                                    const parsed: OcrProcessedFile = JSON.parse(raw);
                                    subscriber.next(parsed);
                                } catch {
                                    // niepełny JSON, czekamy na więcej
                                }
                            }

                            return pump();
                        });

                    return pump();

                })
                .catch(err => {
                    if (err?.name === "AbortError") return;
                    subscriber.error(err);
                });

            return () => controller.abort();
        });
    },

    retryOcrFileProcess: (batchId: number, fileId: number): Observable<OcrProcessedFile> => {
        return new Observable(subscriber => {
            const controller = new AbortController();
            const params = new URLSearchParams();
            params.append('filesIds', String(fileId));

            const url = `${BASE_URL}/ocr/${batchId}/retry?${params.toString()}`;

            authorizedFetch(url, {
                method: 'PUT',
                headers: { Accept: "text/event-stream" },
                signal: controller.signal,
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                if (!response.body) {
                    throw new Error("No response body");
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";

                const pump = (): Promise<void> =>
                    reader.read().then(({ done, value }) => {
                        if (done) {
                            subscriber.complete();
                            return;
                        }

                        buffer += decoder.decode(value, { stream: true });

                        const lines = buffer.split("\n");
                        buffer = lines.pop() ?? "";

                        for (const line of lines) {
                            if (!line.startsWith("data:")) continue;

                            const raw = line.slice("data:".length).trim();
                            if (!raw) continue;

                            try {
                                const parsed: OcrProcessedFile = JSON.parse(raw);
                                subscriber.next(parsed);
                            } catch {
                                // niepełny JSON, czekamy na więcej
                            }
                        }

                        return pump();
                    });

                return pump();

            })
            .catch(err => {
                if (err?.name === "AbortError") return;
                subscriber.error(err);
            });

            return () => controller.abort();
        });
    },

    getOcrHistory: async () => {
        const response = await axios.get<OcrBatchIdResponse[]>('/ocr/conversation/history');
        return response.data;
    },
    deleteOcrBatch: async (id: number) => {
        await axios.delete(`/ocr/conversation/${id}`);
    },

    pinOcrBatch: async (id: number) => {
        await axios.patch(`/ocr/conversation/${id}/pin`, {}, {
            headers: { 'Content-Type': 'application/json' }
        });
    },
    unpinOcrBatch: async (id: number) => {
        await axios.patch(`/ocr/conversation/${id}/unpin`, {}, {
            headers: { 'Content-Type': 'application/json' }
        });
    },

    patchOcrBatchTitle: async (id: number, title: string) => {
        const params = new URLSearchParams({ title });

        await axios.patch(`/ocr/conversation/${id}/title?${params}`);
    },
    exportOcrFile: async (fileId: number, format: OcrFileExportFormat) => {
        const params = new URLSearchParams({ format });

        const response = await axios.post(
            `/ocr/file/${fileId}/export?${params.toString()}`,
            null,
            { responseType: 'blob' }
        );
        return response.data;
    },
};